import { DateTime } from "luxon";
import { defaultDatePickerMinDate, defaultDatePickerMaxDate } from "./datePickerBounds";
import { matchIsValidTel } from "mui-tel-input";

export const isEmpty = (value = "") => value == null || value.length === 0;
export const isEmptyArray = (value = []) => value?.length <= 0;
export const isEmptyObject = (value = {}) => !(Object.keys(value)?.length > 0);

export const getDateTimeValue = (date) => {
  if (!date) return null;
  if (date.isValid !== undefined) return date;

  return DateTime.fromISO(date);
};

export const isDateEmpty = (date) => !date;

export const isInvalidDate = (value = "") => {
  if (!isEmpty(value)) {
    const date = getDateTimeValue(value);

    return !!date && !date.isValid;
  }

  return false;
};

export const isDateBeforeDate = (date, minDate) => {
  const dateTime = getDateTimeValue(date);
  const minDateTime = getDateTimeValue(minDate);
  if (!dateTime || !minDateTime || !dateTime.isValid || !minDateTime.isValid) return false;

  return dateTime.startOf("day") < minDateTime.startOf("day");
};

export const isDateAfterDate = (date, maxDate) => {
  const dateTime = getDateTimeValue(date);
  const maxDateTime = getDateTimeValue(maxDate);
  if (!dateTime || !maxDateTime || !dateTime.isValid || !maxDateTime.isValid) return false;

  return dateTime.startOf("day") > maxDateTime.startOf("day");
};

export const isDateBeforeNow = (date = DateTime.now()) => {
  const dateTime = getDateTimeValue(date);
  return !!dateTime?.isValid && dateTime.startOf("day") < DateTime.now().startOf("day");
};

export const isYearAfterCurrent = (date = DateTime.now()) => {
  const dateTime = getDateTimeValue(date);
  return !!dateTime?.isValid && dateTime.year > DateTime.now().year;
};

export const isTimeBeforeNow = (time = DateTime.now()) => {
  const dateTime = getDateTimeValue(time);
  return !!dateTime?.isValid && dateTime < DateTime.now();
};

export const isNotZero = (value = 0) => value !== 0;
export const isNotZeroOrNegative = (value = 0) => value <= 0;
export const isNotNegative = (value = 0) => value < 0;
export const isNotGreaterThan = (value, limit) => value > limit;
export const isNotLessThan = (value, limit) => value < limit;
export const isNotDecimal = (value = 0) => value % 1 !== 0;

export const isInvalidEmail = (value = "") =>
  value.length > 0 &&
  !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value
  );

export const isInvalidPhoneNumber = (value = "") => {
  return !matchIsValidTel(value, { onlyCountries: ["GR", "CY"] });
};

export const isDateTimeAfterDateTime = (timeToBeCompared, time) => {
  const timeToBeComparedDateTime = DateTime.fromISO(timeToBeCompared);
  const timeDateTime = DateTime.fromISO(time);

  return timeToBeComparedDateTime > timeDateTime;
};

export const isDateTimeBeforeDateTime = (timeToBeCompared, time) => {
  const timeToBeComparedDateTime = DateTime.fromISO(timeToBeCompared);
  const timeDateTime = DateTime.fromISO(time);

  return timeToBeComparedDateTime < timeDateTime;
};

export const isInvalidTime = (value = "") => {
  return isInvalidDate(value);
};

export const parseDateOnly = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = DateTime.fromFormat(value, "yyyy-MM-dd", { zone: "UTC", locale: "en" });
  return date.isValid && date.toFormat("yyyy-MM-dd") === value ? date : null;
};

export const formatDateOnly = (value) => {
  if (typeof value === "string") return parseDateOnly(value) ? value : null;
  return value?.isValid ? value.toFormat("yyyy-MM-dd") : null;
};

export const isValidProfileDocumentDate = (value) => Boolean(
  parseDateOnly(value) && value >= defaultDatePickerMinDate.toISODate() &&
  value <= defaultDatePickerMaxDate.toISODate()
);

export const validateMedicalClearance = (clearance) => {
  if (!clearance) return {};
  const errors = {};
  if (!isValidProfileDocumentDate(clearance.startDate)) errors.startDate = "invalid";
  if (!isValidProfileDocumentDate(clearance.expirationDate)) errors.expirationDate = "invalid";
  if (!errors.startDate && !errors.expirationDate && clearance.startDate > clearance.expirationDate) {
    errors.expirationDate = "reversed";
  }
  return errors;
};

export const validateImageFile = (file, acceptedFileTypes, maxSize = 15 * 1024 * 1024) => {
  if (!file || !Number.isInteger(file.size) || file.size <= 0) return "empty";
  if (file.size > maxSize) return "tooLarge";
  const extensions = acceptedFileTypes?.[file.type];
  const extension = file.name?.toLowerCase().match(/\.[^.]+$/)?.[0];
  return extensions?.includes(extension) ? null : "unsupported";
};

export const validateSelectedImageFile = (value, acceptedFileTypes, maxSize) => {
  if (!value || typeof value === "string") return null;
  return validateImageFile(value, acceptedFileTypes, maxSize);
};

export const formValidator = ({ schema, values }) => {
  const errors = {};
  schema
    .filter((field) => !field.hidden)
    .forEach((field) => {
      if (field.validators) {
        field.validators.forEach(
          ({ validator, text, dependency, specificValue }) => {
            if (dependency) {
              if (validator(values[field.name], values[dependency])) {
                errors[field.name] = text(field.label);
              }
            } else if (specificValue) {
              if (validator(values[field.name], specificValue)) {
                errors[field.name] = text(field.label);
              }
            } else {
              if (validator(values[field.name])) {
                errors[field.name] = text(field.label);
              }
            }
          }
        );
      }
    });

  return errors;
};
